import React, { useState } from 'react';
import {
  Container, Box, Typography, TextField, Button, Link,
  IconButton, Collapse, Grid, Fade, Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import logo from '../images/amaple.jpg'; // Importing the logo

const LandingPage = () => {
  const [tenant, setTenant] = useState('');
  const [error, setError] = useState('');
  const [showTagline, setShowTagline] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);

  const handleGo = () => {
    if (!tenant.match(/^[a-z0-9-]+$/i)) {
      setError('Please enter a valid code (letters, numbers or -).');
      return;
    }
    window.location.href = `https://${tenant}.medicerp.in`;
  };

  const handleLogoError = () => {
    setLogoError(true);
  };

  const handleLocalLogin = () => {
    if (window.location.hostname === 'localhost') {
      // Local development
      window.location.href = '/login?tenant=local';
    } else {
      // Production environment - redirect to main subdomain
      window.location.href = 'https://main.medicerp.in/login';
    }
  };

  const features = [
    { 
      title: "Inventory Management", 
      description: "Track all your medical supplies with real-time inventory updates"
    },
    { 
      title: "Billing & Invoicing", 
      description: "Generate professional invoices and track payments"
    },
    { 
      title: "Supplier Tracking", 
      description: "Manage all your suppliers and purchase orders"
    },
    { 
      title: "Sales Analytics", 
      description: "Get insights into your business performance"
    },
    { 
      title: "Ledger Accounts", 
      description: "Complete management of all financial accounts with double-entry bookkeeping"
    },
    { 
      title: "Financial Statements", 
      description: "Automatically generate Profit & Loss, Balance Sheet, and Cash Flow statements"
    },
    { 
      title: "Role-Based Access", 
      description: "Secure role-based user system with granular permissions control"
    },
    { 
      title: "Prescription Management", 
      description: "Digital prescription tracking with patient history and refill management"
    },
    { 
      title: "Transaction Reconciliation", 
      description: "Automated reconciliation of bank transactions and accounting records"
    },
    { 
      title: "Tax Calculation (GST)", 
      description: "Automatic GST calculation, filing, and compliance tracking"
    },
    { 
      title: "Batch Tracking", 
      description: "Track goods by batch with expiry date monitoring and alerts"
    },
    { 
      title: "HR Management", 
      description: "Manage employee records, payroll, and performance evaluations"
    }
  ];

  return (
    <Box 
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f7ff 0%, #e0edff 100%)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 10% 20%, rgba(25, 118, 210, 0.05) 0%, transparent 20%)',
          zIndex: 0,
        }
      }}
    >
      {/* Decorative elements */}
      <Box sx={{
        position: 'absolute',
        top: -100,
        right: -100,
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(25, 118, 210, 0.08) 0%, transparent 70%)',
        zIndex: 0,
      }} />
      
      <Box sx={{
        position: 'absolute',
        bottom: -150,
        left: -100,
        width: 400,
        height: 400,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(220, 0, 78, 0.05) 0%, transparent 70%)',
        zIndex: 0,
      }} />
      
      <Container 
        maxWidth="md"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flexGrow: 1,
          py: 8,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          {/* Logo with fallback */}
          {logoError ? (
            <Box 
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                boxShadow: '0 8px 24px rgba(25, 118, 210, 0.2)',
              }}
            >
              <Typography 
                variant="h3" 
                component="div" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 700,
                  letterSpacing: -1 
                }}
              >
                M
              </Typography>
            </Box>
          ) : (
            <Box 
              component="img"
              src={logo}
              alt="M" // Alt text set to "M" as requested
              onError={handleLogoError}
              sx={{
                width: 100,
                height: 100,
                objectFit: 'contain',
                margin: '0 auto 24px',
                borderRadius: '50%',
                boxShadow: '0 8px 24px rgba(25, 118, 210, 0.2)',
                border: '2px solid rgba(25, 118, 210, 0.15)',
                backgroundColor: '#1976d2', // Primary color background
                padding: '5px',
              }}
            />
          )}
          
          <Typography 
            variant="h2" 
            component="h1" 
            sx={{ 
              fontWeight: 800, 
              color: '#1976d2',
              mb: 1,
              fontSize: { xs: '2.2rem', sm: '2.5rem', md: '3rem' },
              letterSpacing: '-0.5px'
            }}
          >
            MedicERP
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <IconButton 
              onClick={() => setShowTagline(!showTagline)} 
              size="small"
              sx={{ color: '#1976d2' }}
            >
              {showTagline ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={showTagline} timeout="auto">
            <Typography 
              variant="body1" 
              sx={{ 
                maxWidth: 500, 
                margin: '0 auto',
                mt: 2,
                color: '#4a5568',
                fontStyle: 'italic'
              }}
            >
              A complete, cloud‑hosted ERP solution tailored for retail medical stores.
            </Typography>
          </Collapse>
        </Box>
        
        <Fade in timeout={1000}>
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: 12, 
              p: 4,
              mb: 6,
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(25, 118, 210, 0.15)',
              boxShadow: '0 12px 32px rgba(31, 38, 135, 0.1)'
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1, color: '#2d3748' }}>
                Access Your Medical Store
              </Typography>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                Enter your unique ID to sign in | <Link onClick={handleLocalLogin}sx={{ color: '#1976d2', fontWeight: 500 }} >β - Login Here</Link>
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                placeholder="your-store-code"
                value={tenant}
                onChange={e => { setError(''); setTenant(e.target.value); }}
                error={!!error}
                helperText={error}
                InputProps={{
                  sx: { 
                    height: 50,
                    borderRadius: 3,
                    fontSize: '1.05rem'
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleGo}
                sx={{ 
                  height: 50, 
                  minWidth: 100,
                  borderRadius: 3,
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    transform: 'translateY(-2px)'
                  },
                  fontWeight: 600,
                  fontSize: '1rem',
                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.25)',
                  transition: 'all 0.3s ease',
                }}
              >
                Go
              </Button>
            </Box>
            
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#718096' }}>
              Don't have a store Yet? <Link href="#" underline="hover" sx={{ color: '#1976d2', fontWeight: 500 }}>Contact us</Link> OR
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'center', color: '#718096', mt: 2 }}>
              Want to register your pharmacy? 
              <Link 
                href="/register-tenant" 
                sx={{ color: '#1976d2', fontWeight: 500, ml: 1 }}
              >
                Register Your Pharmacy From Here
              </Link>
            </Typography>
          </Paper>
        </Fade>
        
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button 
            variant="outlined" 
            onClick={() => setShowFeatures(!showFeatures)}
            endIcon={showFeatures ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ 
              borderRadius: 3,
              borderColor: '#1976d2',
              color: '#1976d2',
              fontWeight: 600,
              padding: '8px 24px',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.05)',
                borderColor: '#1565c0',
                borderWidth: '2px'
              }
            }}
          >
            {showFeatures ? 'Hide Features' : 'Explore Features'}
          </Button>
        </Box>
        
        <Collapse in={showFeatures} timeout="auto">
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Fade in timeout={(index + 1) * 300}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      height: '100%', 
                      borderRadius: 3,
                      border: '1px solid rgba(25, 118, 210, 0.15)',
                      background: 'rgba(255, 255, 255, 0.8)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 16px 24px rgba(25, 118, 210, 0.15)',
                        borderColor: '#1976d2'
                      }
                    }}
                  >
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1.5, color: '#1976d2' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4a5568' }}>
                      {feature.description}
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Collapse>
        
        <Box sx={{ textAlign: 'center', mt: 'auto', pt: 4 }}>
          <Typography variant="body2" sx={{ color: '#718096' }}>
            © {new Date().getFullYear()} MedicERP. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
            <Link href="#" variant="body2" sx={{ color: '#718096', '&:hover': { color: '#1976d2' } }} underline="hover">Privacy</Link>
            <Link href="#" variant="body2" sx={{ color: '#718096', '&:hover': { color: '#1976d2' } }} underline="hover">Terms</Link>
            <Link href="#" variant="body2" sx={{ color: '#718096', '&:hover': { color: '#1976d2' } }} underline="hover">Contact</Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingPage;
